import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { notifyClient } from "@/lib/notify";
import { shipperLoadLink } from "@/lib/lrDirect/notifyLinks";

/**
 * Phase 2 — broker_accepted dispatch event.
 *
 * Idempotent: only the first acceptance writes a row. Subsequent calls bail
 * gracefully so the "Accept Dispatch" button is safe to re-click.
 */

export function useHasBrokerAccepted(dispatchId: string | undefined) {
  const [accepted, setAccepted] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!dispatchId) {
      setAccepted(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("direct_dispatch_events" as any)
      .select("id")
      .eq("dispatch_id", dispatchId)
      .eq("event_type", "broker_accepted")
      .limit(1)
      .maybeSingle();
    setAccepted(!!data);
    setLoading(false);
  }, [dispatchId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { accepted, loading, refresh };
}

export interface AcceptDispatchArgs {
  dispatchId: string;
  loadId: string;
  brokerId: string;
  shipperUserId: string | null;
  loadNumber?: string | null;
  /** Current dispatch status — when 'ready', accepting also bumps to 'assigned'. */
  currentStatus?: string | null;
}

export async function acceptDispatch({
  dispatchId, loadId, brokerId, shipperUserId, loadNumber, currentStatus,
}: AcceptDispatchArgs): Promise<{ alreadyAccepted: boolean }> {
  // Idempotency check
  const { data: existing } = await supabase
    .from("direct_dispatch_events" as any)
    .select("id")
    .eq("dispatch_id", dispatchId)
    .eq("event_type", "broker_accepted")
    .limit(1)
    .maybeSingle();
  if (existing) return { alreadyAccepted: true };

  const { error } = await supabase
    .from("direct_dispatch_events" as any)
    .insert({
      dispatch_id: dispatchId,
      actor_id: brokerId,
      event_type: "broker_accepted",
      payload: { accepted_at: new Date().toISOString() },
    });
  if (error) throw error;

  // Best-effort lane lookup for notification copy.
  let lane = "";
  try {
    const { data: dl } = await supabase
      .from("direct_loads" as any)
      .select("origin_city, origin_state, destination_city, destination_state")
      .eq("id", loadId)
      .maybeSingle();
    if (dl) {
      const d: any = dl;
      lane = `${d.origin_city}, ${d.origin_state} → ${d.destination_city}, ${d.destination_state}`;
    }
  } catch { /* noop */ }

  if (shipperUserId) {
    notifyClient({
      recipientUserId: shipperUserId,
      eventType: "broker_accepted",
      inAppType: "dispatch_broker_accepted",
      title: `📦 Load Update`,
      body: ["Broker accepted", lane].filter(Boolean).join(" · "),
      link: shipperLoadLink(loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, load_type: "lr_direct" },
    });
  }


  // CANONICAL: acceptance is represented ONLY by the `broker_accepted` event
  // above. It must never write `status_assigned` — deriveStages() treats that
  // event as "Truck En Route", so accepting a packet would have falsely
  // advanced the tracker. We still bump the DERIVED legacy status column from
  // 'ready' → 'assigned' (badges / My Loads buckets read it), but we write no
  // status_* event, so no milestone is implied.
  if (currentStatus === "ready") {
    const nowIso = new Date().toISOString();
    const { error: bumpErr } = await supabase
      .from("direct_dispatches" as any)
      .update({ status: "assigned", status_updated_at: nowIso, status_updated_by: brokerId })
      .eq("id", dispatchId)
      .select("id");
    if (bumpErr) throw bumpErr;
  }

  return { alreadyAccepted: false };
}

